import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { forkJoin, map, Observable, switchMap, tap } from 'rxjs';
import { CatalogsService } from '@shared/services/catalogs.service';
import { prepareCatalog } from '@shared/utils/catalogs';
import { FetchUpdates } from './catalogs.actions';
import {
  CatalogsStateModel,
  CommonCatalog,
  CustomCatalog,
  CustomItemText,
  NsiChangeReason,
  NsiCountry,
  NsiCurrency,
  NsiCustoms,
  NsiCustomsPoint,
  NsiCustomsStorage,
  NsiDealCharacter,
  NsiDealFeature,
  NsiGoodsCategory,
  NsiGuaranteeCode,
  NsiMeasureUnit,
  NsiNoGuaranteeCauseCode,
  NsiPreference,
  NsiPresentAddType,
  NsiPresentType,
  NsiRegion,
  NsiTransportCategory,
  NsiTransportMark,
  NsiTypeDocument,
} from '../../types/catalog';

type CatalogUpdateRequests = Record<string, Observable<any>>;

const defaultState = () => {
  return {
    nsiCountry: null,
    nsiDealCharacterSD: null,
    nsiDealFeatureSD: null,
    nsiDealCharacterDT: null,
    nsiDealFeatureDT: null,
    nsiTypeDelivery: null,
    nsiCurrency: null,
    nsiCustoms: null,
    nsiMeasureUnit: null,
    nsiTypeDocument: null,
    nsiCancellationReason: null,
    nsiCorrectionReason: null,
    nsiRegion: null,
    nsiIdentityDocument: null,
    nsiCustomsCostMethod: null,
    nsiMovementFeature: null,
    nsiPackageType: null,
    nsiPaymentMode: null,
    nsiPaymentType: null,
    nsiPreference: null,
    nsiPresentType: null,
    nsiPresentAddType: null,
    nsiTransportType: null,
    nsiTransportTypeInternational: null,
    nsiPurposePlacement: null,
    nsiCustomsStorage: null,
    nsiCustomsStorageType: null,
    nsiCustomsProcedure: null,
    nsiDeliveryTerm: null,
    nsiCustomsPoint: null,
    nsiChangeReason: null,
    nsiGoodsCategory: null,
    nsiTransportCategory: null,
    nsiRecyclingRate: null,
    nsiTransportMark: null,
    nsiGuaranteeCode: null,
    nsiNoGuaranteeCode: null,
  };
};
@State<CatalogsStateModel>({
  name: 'catalogs',
  defaults: defaultState(),
})
@Injectable()
export default class CatalogsState {
  constructor(private readonly catalogs: CatalogsService) {}

  @Action(FetchUpdates)
  fetchUpdates(ctx: StateContext<CatalogsStateModel>) {
    return this.catalogs.getLastUpdated().pipe(
      map(updates => {
        const providedCatalogs = defaultState();
        const catalogsState = ctx.getState();
        const requests: CatalogUpdateRequests = {};
        Object.keys(updates).forEach(key => {
          const catalogName = key as keyof CatalogsStateModel;
          if (catalogName in providedCatalogs) {
            if (
              catalogsState[catalogName] === null ||
              catalogsState[catalogName]?.updated !== updates[catalogName]
            ) {
              console.log(
                catalogsState[catalogName]?.updated,
                updates[catalogName],
              );
              console.log('FETCH CATALOG UPDATE >>>', catalogName);
              requests[catalogName] = this.catalogs.fetchCatalog(catalogName);
            }
          }
        });
        return requests;
      }),
      switchMap(requests => forkJoin(requests)),
      tap(catalogs => {
        ctx.patchState(catalogs);
      }),
    );
  }

  @Selector()
  static getNsiCustoms(
    state: CatalogsStateModel,
  ): CustomCatalog<NsiCustoms & CustomItemText> {
    return prepareCatalog<NsiCustoms>(state.nsiCustoms).map(customs => ({
      ...customs,
      itemText: `${customs.code} - ${customs.name}`,
    }));
  }

  @Selector()
  static getNsiCountries(
    state: CatalogsStateModel,
  ): CustomCatalog<NsiCountry & CustomItemText> {
    return prepareCatalog<NsiCountry>(state.nsiCountry).map(country => ({
      ...country,
      itemText: `${country.letterCode} - ${country.name}`,
    }));
  }

  @Selector()
  static getNsiRegions(
    state: CatalogsStateModel,
  ): CustomCatalog<NsiRegion & CustomItemText> {
    return prepareCatalog<NsiRegion>(state.nsiRegion).map(region => ({
      ...region,
      itemText: `${region.code} - ${region.name}`,
    }));
  }

  @Selector()
  static getNsiDealCharactersSD(state: CatalogsStateModel) {
    return prepareCatalog<NsiDealCharacter>(state.nsiDealCharacterSD).map(
      deal => ({
        ...deal,
        itemText: `${deal.code} - ${deal.name}`,
      }),
    );
  }

  @Selector()
  static getNsiDealCharactersDT(state: CatalogsStateModel) {
    return prepareCatalog<NsiDealCharacter>(state.nsiDealCharacterDT).map(
      deal => ({
        ...deal,
        itemText: `${deal.code} - ${deal.name}`,
      }),
    );
  }

  @Selector()
  static getNsiDealFeaturesSD(state: CatalogsStateModel) {
    return prepareCatalog<NsiDealFeature>(state.nsiDealFeatureSD).map(
      feature => ({
        ...feature,
        itemText: `${feature.code} - ${feature.name}`,
      }),
    );
  }

  @Selector()
  static getNsiDealFeaturesDT(state: CatalogsStateModel) {
    return prepareCatalog<NsiDealFeature>(state.nsiDealFeatureDT).map(
      feature => ({
        ...feature,
        itemText: `${feature.code} - ${feature.name}`,
      }),
    );
  }

  @Selector()
  static getNsiTypesOfDelivery(state: CatalogsStateModel) {
    return prepareCatalog<CommonCatalog>(state.nsiTypeDelivery).map(
      delivery => ({
        ...delivery,
        itemText: `${delivery.code} - ${delivery.name}`,
      }),
    );
  }

  @Selector()
  static getNsiTypesOfDocument(state: CatalogsStateModel) {
    return prepareCatalog<NsiTypeDocument>(state.nsiTypeDocument).map(
      document => ({
        ...document,
        itemText: `${document.code} - ${document.name}`,
      }),
    );
  }

  @Selector()
  static getNsiCurrencies(
    state: CatalogsStateModel,
  ): CustomCatalog<NsiCurrency & CustomItemText> {
    return prepareCatalog<NsiCurrency>(state.nsiCurrency).map(currency => ({
      ...currency,
      itemText: `${currency.letterCode} - ${currency.name}`,
    }));
  }

  @Selector()
  static getNsiMeasureUnits(state: CatalogsStateModel) {
    return prepareCatalog<NsiMeasureUnit>(state.nsiMeasureUnit);
  }

  @Selector()
  static getNsiCorrectionReasons(
    state: CatalogsStateModel,
  ): CustomCatalog<CommonCatalog & CustomItemText> {
    return prepareCatalog<CommonCatalog>(state.nsiCorrectionReason).map(
      reason => ({
        ...reason,
        itemText: `${reason.code} - ${reason.name}`,
      }),
    );
  }

  @Selector()
  static getNsiCancellationReasons(
    state: CatalogsStateModel,
  ): CustomCatalog<CommonCatalog & CustomItemText> {
    return prepareCatalog<CommonCatalog>(state.nsiCancellationReason).map(
      reason => ({
        ...reason,
        itemText: `${reason.code} - ${reason.name}`,
      }),
    );
  }

  @Selector()
  static getNsiIdentityDocuments(state: CatalogsStateModel) {
    return prepareCatalog<CommonCatalog>(state.nsiIdentityDocument);
  }

  @Selector()
  static getNsiTransportCategory(state: CatalogsStateModel) {
    return prepareCatalog<NsiTransportCategory>(state.nsiTransportCategory).map(
      category => ({
        ...category,
        itemText: `${category.code} - ${category.description}`,
      }),
    );
  }

  @Selector()
  static getNsiRecyclingRate(state: CatalogsStateModel) {
    return state.nsiRecyclingRate?.list ?? [];
  }

  @Selector()
  static getNsiCustomsCostMethod(
    state: CatalogsStateModel,
  ): CustomCatalog<CommonCatalog & CustomItemText> {
    const prepared = prepareCatalog<CommonCatalog>(
      state.nsiCustomsCostMethod,
    ).map(method => ({
      ...method,
      itemText: `${method.code} - ${method.name}`,
    }));

    return structuredClone(prepared).sort((a, b) => {
      if (a.code === '0') {
        return 1;
      }

      if (b.code === '0') {
        return -1;
      }

      if (a.code < b.code) {
        return -1;
      }
      if (a.code > b.code) {
        return 1;
      }
      return 0;
    });
  }

  @Selector()
  static getNsiMovementFeature(state: CatalogsStateModel) {
    return prepareCatalog<CommonCatalog>(state.nsiMovementFeature);
  }

  @Selector()
  static getNsiPackageType(
    state: CatalogsStateModel,
  ): CustomCatalog<CommonCatalog & CustomItemText> {
    return prepareCatalog<CommonCatalog>(state.nsiPackageType).map(
      packageType => ({
        ...packageType,
        itemText: `${packageType.code} - ${packageType.name}`,
      }),
    );
  }

  @Selector()
  static getNsiPaymentMode(state: CatalogsStateModel) {
    return prepareCatalog<CommonCatalog>(state.nsiPaymentMode);
  }

  @Selector()
  static getNsiTransportMark(state: CatalogsStateModel) {
    return prepareCatalog<NsiTransportMark>(state.nsiTransportMark);
  }

  @Selector()
  static getNsiPaymentType(
    state: CatalogsStateModel,
  ): CustomCatalog<CommonCatalog & CustomItemText> {
    return prepareCatalog<CommonCatalog>(state.nsiPaymentType).map(
      paymentType => ({
        ...paymentType,
        itemText: `${paymentType.code} - ${paymentType.name}`,
      }),
    );
  }

  @Selector()
  static getNsiPreference(state: CatalogsStateModel) {
    const prepared = prepareCatalog<NsiPreference>(state.nsiPreference);
    return structuredClone(prepared).sort((a, b) => {
      if (a.code === 'ОО') {
        return -1;
      }
      if (b.code === 'ОО') {
        return 1;
      }
      return a.code.localeCompare(b.code);
    });
  }

  @Selector()
  static getNsiPresentType(state: CatalogsStateModel) {
    return prepareCatalog<NsiPresentType>(state.nsiPresentType);
  }

  @Selector()
  static getNsiPresentAddType(state: CatalogsStateModel) {
    const prepared = prepareCatalog<NsiPresentAddType>(state.nsiPresentAddType);
    return structuredClone(prepared).sort((a, b) => {
      return a.code.localeCompare(b.code);
    });
  }

  @Selector()
  static getNsiTransportType(
    state: CatalogsStateModel,
  ): CustomCatalog<CommonCatalog & CustomItemText> {
    return prepareCatalog<CommonCatalog>(state.nsiTransportType).map(
      transportType => ({
        ...transportType,
        itemText: `${transportType.code} - ${transportType.name}`,
      }),
    );
  }

  @Selector()
  static getNsiTransportTypeInternational(
    state: CatalogsStateModel,
  ): CustomCatalog<CommonCatalog & CustomItemText> {
    return prepareCatalog<CommonCatalog>(
      state.nsiTransportTypeInternational,
    ).map(transportType => ({
      ...transportType,
      itemText: `${transportType.code} - ${transportType.name}`,
    }));
  }

  @Selector()
  static getNsiPurposePlacement(
    state: CatalogsStateModel,
  ): CustomCatalog<CommonCatalog & CustomItemText> {
    return prepareCatalog<CommonCatalog>(state.nsiPurposePlacement).map(
      item => ({
        ...item,
        itemText: `${item.code} - ${item.name}`,
      }),
    );
  }

  @Selector()
  static getNsiCustomsStorage(state: CatalogsStateModel) {
    return prepareCatalog<NsiCustomsStorage>(state.nsiCustomsStorage);
  }

  @Selector()
  static getNsiCustomsStorageType(
    state: CatalogsStateModel,
  ): CustomCatalog<CommonCatalog & CustomItemText> {
    return prepareCatalog<CommonCatalog>(state.nsiCustomsStorageType).map(
      packageType => ({
        ...packageType,
        itemText: `${packageType.code} - ${packageType.name}`,
      }),
    );
  }

  @Selector()
  static getNsiCustomsProcedure(state: CatalogsStateModel) {
    return prepareCatalog<CommonCatalog>(state.nsiCustomsProcedure);
  }

  @Selector()
  static getNsiDeliveryTerm(state: CatalogsStateModel) {
    return prepareCatalog<CommonCatalog>(state.nsiDeliveryTerm);
  }

  @Selector()
  static getNsiCustomsPoint(
    state: CatalogsStateModel,
  ): CustomCatalog<NsiCustomsPoint & CustomItemText> {
    return prepareCatalog<NsiCustomsPoint>(state.nsiCustomsPoint).map(
      point => ({
        ...point,
        itemText: `${point.code} - ${point.name}`,
      }),
    );
  }

  @Selector()
  static getNsiGoodsCategory(
    state: CatalogsStateModel,
  ): CustomCatalog<NsiGoodsCategory & CustomItemText> {
    return prepareCatalog<NsiGoodsCategory>(state.nsiGoodsCategory).map(
      category => ({
        ...category,
        itemText: `${category.code} - ${category.name}`,
      }),
    );
  }

  @Selector()
  static getNsiChangeReason(
    state: CatalogsStateModel,
  ): CustomCatalog<NsiChangeReason & CustomItemText> {
    return prepareCatalog<NsiChangeReason>(state.nsiChangeReason).map(
      reason => ({
        ...reason,
        itemText: `${reason.code} - ${reason.name}`,
      }),
    );
  }

  @Selector()
  static getNsiGuaranteeCode(
    state: CatalogsStateModel,
  ): CustomCatalog<NsiGuaranteeCode & CustomItemText> {
    return prepareCatalog<NsiGuaranteeCode>(state.nsiGuaranteeCode).map(
      reason => ({
        ...reason,
        itemText: `${reason.code} - ${reason.name}`,
      }),
    );
  }

  @Selector()
  static getNsiNoGuaranteeCauseCode(
    state: CatalogsStateModel,
  ): CustomCatalog<NsiNoGuaranteeCauseCode & CustomItemText> {
    return prepareCatalog<NsiNoGuaranteeCauseCode>(
      state.nsiNoGuaranteeCode,
    ).map(reason => ({
      ...reason,
      itemText: `${reason.code} - ${reason.name}`,
    }));
  }
}
